export interface Pipeline {
  id: string;
  label: string;
  stages: { id: string; label: string }[];
}

export interface DrillDownDeal {
  hubspot_deal_id: string;
  dealname: string | null;
  amount: number | null;
  amount_in_home_currency: number | null;
  net_price: number | null;
  dealstage: string | null;
  pipeline: string | null;
  closedate: string | null;
  order_date: string | null;
  free_quote_form_filled: string | null;
  type_of_error: string | null;
  accuracy_error: string | null;
  error_notes: string | null;
  hubspot_owner_id: string | null;
  hs_last_modified_date: string | null;
  hubspot_createdate: string | null;
  owner_name: string | null;
  metric_source?: string;
}

export type MetricType =
  | "opportunities"
  | "closedDeals"
  | "errors"
  | "grossSales"
  | "netSales"
  | "closeRate";

export const METRIC_LABELS: Record<MetricType, string> = {
  opportunities: "Opportunities",
  closedDeals: "Closed Deals",
  errors: "Errors (Consultant)",
  grossSales: "Gross Sales",
  netSales: "Net Sales",
  closeRate: "Close Rate",
};

export function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatDate(date: string | null): string {
  if (!date) return "—";
  try {
    // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
    // (new Date("2026-01-01") is UTC midnight, which displays as Dec 31 in US timezones)
    const [year, month, day] = date.split("-").map(Number);
    if (!year || !month || !day) return "—";
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

// ── Fiscal Year Utilities ────────────────────────────────────────────

export type FYQuarter = "Q1" | "Q2" | "Q3" | "Q4" | "FY";

export function getFiscalYearDates(fy: number, quarter: FYQuarter): { start: string; end: string } {
  const fyStart = fy - 1;
  switch (quarter) {
    case "Q1":
      return { start: `${fyStart}-04-01`, end: `${fyStart}-06-30` };
    case "Q2":
      return { start: `${fyStart}-07-01`, end: `${fyStart}-09-30` };
    case "Q3":
      return { start: `${fyStart}-10-01`, end: `${fyStart}-12-31` };
    case "Q4":
      return { start: `${fy}-01-01`, end: `${fy}-03-31` };
    case "FY":
      return { start: `${fyStart}-04-01`, end: `${fy}-03-31` };
  }
}

export function getCurrentFiscalQuarter(): { fy: number; quarter: FYQuarter } {
  const now = new Date();
  const month = now.getMonth();
  const calYear = now.getFullYear();
  if (month >= 3 && month <= 5) return { fy: calYear + 1, quarter: "Q1" };
  if (month >= 6 && month <= 8) return { fy: calYear + 1, quarter: "Q2" };
  if (month >= 9 && month <= 11) return { fy: calYear + 1, quarter: "Q3" };
  return { fy: calYear, quarter: "Q4" };
}

export const FY_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const { fy } = getCurrentFiscalQuarter();
  return fy - 4 + i;
});

/** Returns the 12 fiscal-year months (Apr–Mar) as YYYY-MM strings. */
function getFiscalYearMonths(fy: number): string[] {
  const fyStart = fy - 1;
  const months: string[] = [];
  for (let m = 4; m <= 12; m++) {
    months.push(`${fyStart}-${String(m).padStart(2, "0")}`);
  }
  for (let m = 1; m <= 3; m++) {
    months.push(`${fy}-${String(m).padStart(2, "0")}`);
  }
  return months;
}

/** Returns the fiscal quarter label (Q1-Q4) for a YYYY-MM string. */
function getFiscalQuarterForMonth(yearMonth: string): "Q1" | "Q2" | "Q3" | "Q4" {
  const month = Number.parseInt(yearMonth.split("-")[1]!, 10);
  if (month >= 4 && month <= 6) return "Q1";
  if (month >= 7 && month <= 9) return "Q2";
  if (month >= 10 && month <= 12) return "Q3";
  return "Q4";
}

/** Format a month label from YYYY-MM to "Apr", "May", etc. */
export function formatMonthLabel(yearMonth: string): string {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(y!, m! - 1, 1).toLocaleDateString("en-US", { month: "short" });
}

/** Resolve pipeline IDs and stage exclusions from pipeline metadata. */
export function resolvePipelineIds(pipelines: Pipeline[]): {
  salesPipelineId: string | null;
  prodPipelineId: string | null;
  excludeOppStageIds: string[];
  excludeClosedStageIds: string[];
} {
  let salesId: string | null = null;
  let prodId: string | null = null;
  let returnedRefundedId: string | null = null;

  for (const p of pipelines) {
    const label = p.label.toLowerCase();
    if (label.includes("sales") && !salesId) {
      salesId = p.id;
    }
    if (label.includes("production") && !prodId) {
      prodId = p.id;
      for (const s of p.stages) {
        const sLabel = s.label.toLowerCase();
        if (sLabel.includes("returned") || sLabel.includes("refunded")) {
          returnedRefundedId = s.id;
        }
      }
    }
  }

  const closedIds: string[] = [];
  if (returnedRefundedId) closedIds.push(returnedRefundedId);

  return {
    salesPipelineId: salesId,
    prodPipelineId: prodId,
    excludeOppStageIds: [],
    excludeClosedStageIds: closedIds,
  };
}

export function getPipelineName(pipelineId: string | null, pipelines: Pipeline[]): string {
  if (!pipelineId) return "—";
  const p = pipelines.find((p) => p.id === pipelineId);
  return p ? p.label : pipelineId;
}

export function getStageName(stageId: string | null, pipelines: Pipeline[]): string {
  if (!stageId) return "—";
  for (const p of pipelines) {
    const stage = p.stages.find((s) => s.id === stageId);
    if (stage) return stage.label;
  }
  return stageId;
}
